import React from "react";
import { Wand2, ScrollText, Users, Dice6, Compass, ShieldCheck } from "lucide-react";
import SeoLandingTemplate from "./SeoLandingTemplate";

/**
 * /en/dungeon-master-tools · /it/dungeon-master-tools
 * SEO landing for the "Dungeon Master tools" query cluster.
 */
const data = {
  slug: "dungeon-master-tools",
  path: "/dungeon-master-tools",
  seoTitle: {
    en: "Dungeon Master Tools — everything to prep and run a TTRPG campaign",
    it: "Strumenti per Dungeon Master — tutto per preparare e condurre una campagna GDR",
  },
  seoDescription: {
    en: "The complete Dungeon Master toolkit: fantasy map maker, Virtual Tabletop, AI narrative assistant and campaign organiser. Mytharion is built by a DM, for DMs.",
    it: "Il set completo per Dungeon Master: creatore di mappe fantasy, Virtual Tabletop, assistente narrativo AI e organizzatore di campagne. Mytharion è costruito da un DM, per i DM.",
  },
  eyebrow: { en: "Mytharion · Toolkit for DMs", it: "Mytharion · Toolkit per DM" },
  h1: {
    en: "Dungeon Master Tools that respect your prep time",
    it: "Strumenti per Dungeon Master che rispettano il tuo tempo di prep",
  },
  lead: [
    {
      en: "Running a campaign shouldn't mean juggling ten different apps. Mytharion brings the map maker, the Virtual Tabletop and the narrative assistant into one ecosystem so you can focus on the story.",
      it: "Condurre una campagna non dovrebbe voler dire gestire dieci applicazioni diverse. Mytharion riunisce il creatore di mappe, il Virtual Tabletop e l'assistente narrativo in un solo ecosistema, così puoi concentrarti sulla storia.",
    },
  ],
  primaryCta: {
    href: "/store",
    label: { en: "See all DM tools", it: "Vedi tutti gli strumenti" },
  },
  secondaryCta: {
    href: "/mytharion-vtt",
    label: { en: "Try the VTT", it: "Prova il VTT" },
  },
  featuresTitle: { en: "What's in the toolkit", it: "Cosa contiene il toolkit" },
  features: [
    {
      icon: Compass,
      title: { en: "Fantasy map maker", it: "Creatore di mappe fantasy" },
      desc: {
        en: "Cartomancer: continent-to-dungeon maps with markers, layers and hidden DM notes.",
        it: "Cartomancer: mappe dal continente al dungeon con marker, livelli e note segrete per il DM.",
      },
    },
    {
      icon: Users,
      title: { en: "Online Virtual Tabletop", it: "Virtual Tabletop online" },
      desc: {
        en: "MytharionVTT: real-time maps, tokens, chat, initiative and dice for you and your players.",
        it: "MytharionVTT: mappe in tempo reale, token, chat, iniziativa e dadi per te e i tuoi giocatori.",
      },
    },
    {
      icon: Wand2,
      title: { en: "AI Loremaster", it: "AI Loremaster" },
      desc: {
        en: "Improvise names, prophecies, NPC voices and encounters in seconds when the session goes off-rails.",
        it: "Improvvisa nomi, profezie, voci degli NPC e incontri in pochi secondi quando la sessione devia.",
      },
    },
    {
      icon: ScrollText,
      title: { en: "AI narrative journal", it: "Diario narrativo AI" },
      desc: {
        en: "Turn scattered session notes into a coherent campaign chronicle your players will actually read.",
        it: "Trasforma appunti sparsi di sessione in una cronaca coerente della campagna che i giocatori leggeranno davvero.",
      },
    },
    {
      icon: Dice6,
      title: { en: "Shared dice", it: "Dadi condivisi" },
      desc: {
        en: "d4 → d100, advantage/disadvantage, modifiers and a shared log so nobody can \"forget\" a roll.",
        it: "d4 → d100, vantaggio/svantaggio, modificatori e cronologia condivisa: nessuno \"dimentica\" un tiro.",
      },
    },
    {
      icon: ShieldCheck,
      title: { en: "DM-only content", it: "Contenuti solo per il DM" },
      desc: {
        en: "Every location and NPC has private DM fields that never leak to players — even inside the VTT.",
        it: "Ogni luogo e NPC ha campi privati per il DM che non trapelano mai ai giocatori — nemmeno nel VTT.",
      },
    },
  ],
  how: [
    {
      title: { en: "Prep the world", it: "Prepara il mondo" },
      desc: {
        en: "Draw regions, cities and dungeons in Cartomancer; attach NPCs, loot and secrets to every marker.",
        it: "Disegna regioni, città e dungeon in Cartomancer; allega NPC, tesori e segreti a ogni marker.",
      },
    },
    {
      title: { en: "Invite your players", it: "Invita i giocatori" },
      desc: {
        en: "Send an invite code, players jump into MytharionVTT and share the same table with you.",
        it: "Manda un codice invito, i giocatori entrano in MytharionVTT e condividono il tuo tavolo.",
      },
    },
    {
      title: { en: "Run the session", it: "Conduci la sessione" },
      desc: {
        en: "Move tokens, roll dice, chat, reveal fog of war, ping the AI Loremaster when improvisation kicks in.",
        it: "Muovi i token, tira i dadi, chatta, rivela la nebbia di guerra, chiama l'AI Loremaster quando serve improvvisare.",
      },
    },
  ],
  benefits: [
    { en: "No monthly subscription required to start", it: "Nessun abbonamento mensile richiesto per iniziare" },
    { en: "Everything integrated — no more copy-pasting between apps", it: "Tutto integrato — basta copia-incolla tra app diverse" },
    { en: "Built by a DM who ships weekly", it: "Costruito da un DM con release settimanali" },
    { en: "Active Discord community for feedback and support", it: "Community Discord attiva per feedback e supporto" },
    { en: "Data stays yours — you can export and delete anytime", it: "I dati restano tuoi — puoi esportare ed eliminare quando vuoi" },
    { en: "Works in your browser, no installation", it: "Funziona nel browser, nessuna installazione" },
  ],
  faq: [
    {
      q: { en: "Is Mytharion free for Dungeon Masters?", it: "Mytharion è gratis per i Dungeon Master?" },
      a: {
        en: "Cartomancer Basic is a one-time €7.99 purchase. MytharionVTT is currently in free Beta with an invite code. Premium features (AI, timeline, wiki) are €2.99/month.",
        it: "Cartomancer Basic costa €7.99 una tantum. MytharionVTT è attualmente in Beta gratuita con codice invito. Le funzionalità Premium (AI, timeline, wiki) sono €2.99 al mese.",
      },
    },
    {
      q: { en: "Do my players need to pay?", it: "I miei giocatori devono pagare?" },
      a: {
        en: "No. Players only need a free account to join a VTT table via the DM's invite code.",
        it: "No. I giocatori hanno bisogno solo di un account gratuito per unirsi a un tavolo VTT con il codice invito del DM.",
      },
    },
    {
      q: { en: "Which TTRPG systems are supported?", it: "Quali GDR sono supportati?" },
      a: {
        en: "Anything grid-based or theatre-of-mind: D&D 5e, Pathfinder, OSR, Call of Cthulhu, homebrew.",
        it: "Qualunque gioco basato su griglia o \"theatre of mind\": D&D 5e, Pathfinder, OSR, Il richiamo di Cthulhu, homebrew.",
      },
    },
    {
      q: { en: "Can I move a running campaign into Mytharion?", it: "Posso spostare una campagna in corso in Mytharion?" },
      a: {
        en: "Yes. Start with Cartomancer to rebuild the world map, then invite your players into MytharionVTT to run future sessions online.",
        it: "Sì. Parti da Cartomancer per ricostruire la mappa del mondo, poi invita i giocatori in MytharionVTT per condurre le prossime sessioni online.",
      },
    },
  ],
  finalCta: {
    href: "/store",
    headline: {
      en: "Prep less, play more — the Mytharion DM toolkit awaits",
      it: "Meno prep, più gioco — il toolkit DM Mytharion ti aspetta",
    },
    label: { en: "Explore the tools", it: "Esplora gli strumenti" },
  },
  software: { name: "Mytharion", category: "GameApplication" },
};

export default function DungeonMasterToolsLanding() {
  return <SeoLandingTemplate data={data} />;
}
